import {
  TextInput,
  Box,
  ScrollArea,
  Popover,
  Card,
  Group,
  Flex,
  Text,
  Spoiler,
  Skeleton,
  Stack,
  Tooltip,
  Checkbox
} from "@mantine/core";
import {IconMessage, IconSearch} from "@tabler/icons";
import { useEffect, useState } from "react";

import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import {useClickOutside, useHover} from "@mantine/hooks";
import { searchIdeas } from "../../../Api";
import { getBagelId } from "../../../utils";

const Search = ({query, setQuery}:{query: string, setQuery: React.Dispatch<React.SetStateAction<string>>;}) => {
  const auth0 = useAuth0();
  
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [opened, setOpened] = useState(false);
  const [loadingSearchResults, setLoadingSearchResults] = useState(false);
  const [onlyMyIdeas, setOnlyMyIdeas] = useState(false);
  const ref = useClickOutside(() => setOpened(false));
  const userId = getBagelId(auth0.user);

  const navigate = useNavigate();
  const doSearch = async () => {
    setLoadingSearchResults(true)
    searchIdeas({ query, onlyMyIdeas: onlyMyIdeas ? userId : undefined}, auth0)
      .then((results: any[]) => {
        setSearchResults(results || []);
        setLoadingSearchResults(false)
      })
      .catch((e) => console.log(e));
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (query !== "") {
        doSearch();
      } else {
        setSearchResults([]);
      }
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [query,onlyMyIdeas]);

  return (
    <Popover opened={opened} width={600} position="bottom-end" offset={20}>
      <Popover.Target>
        <Tooltip label="Search Ideas across the organization" withArrow>
          <TextInput
            sx={{
              input: {
                background: "#363699",
                color: "#fff",
              },
            }}
            placeholder="Search"
            variant="filled"
            radius="md"
            icon={<IconSearch size={14.5} />}
            onFocus={() => setOpened(true)}
            onClick={() => setOpened(true)}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown p={0} ml={66} >
        <Box ref={ref} bg="#F8F8FB">
          <Checkbox
            sx={{ padding: "0 15px",paddingTop: "5px",borderBottom: "1px solid #D9D9D9" }}
            label="Only search Ideas I own"
            color="indigo"
            checked={onlyMyIdeas}
            onChange={(event) => setOnlyMyIdeas (event.currentTarget.checked)}
            py="xs"
          />
          <ScrollArea
            style={{ height: 300, overflowX: "unset" }}
            px={"md"}
            pb={0}
          >
            { !loadingSearchResults?
              searchResults.map((idea: any) => (
                <IdeaSearchResult
                  idea={idea}
                  onClick={() => {
                    navigate(`/idea/${idea._id}`);
                    setOpened(false);
                  }}
                />
              ))
              :
              <Stack w="99%"  mt={8} mr={123}>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton
                    p={12}
                    key={i}
                    height={124}
                    mb={0}
                  />
                ))}
              </Stack>
            }
          </ScrollArea>
          <Box fz="14px" sx={{ borderTop: "1px solid #D9D9D9" }} p={"md"}>
            {" "}
            {searchResults.length} results found{" "}
          </Box>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};


function IdeaSearchResult({
  idea,
  onClick,
}: {
  idea: any;
  onClick: () => void;
}) {
  const { hovered, ref } = useHover();
  return (
    <Card
      ref={ref}
      withBorder
      p="xs"
      my="xs"
      sx={{
        ":hover": {
          backgroundColor: "#5C5CEA33",
          color: "black",
          cursor: "pointer",
        },
      }}
      onClick={onClick}
    >
      <Group position="apart">
        <Flex align="center" h={26}>
          <IconMessage color="#5B60E3" />
          <Box ml="sm" mr="sm">
            <Text w='470px' lineClamp={1} fz="sm">
              {idea?.title || "Unassigned"}
            </Text>
          </Box>
        </Flex>
      </Group>

      <Box w='530px'>
        <Spoiler
          maxHeight={20}
          showLabel="see more"
          hideLabel="Hide"
          sx={(theme) => ({
            fontSize: theme.fontSizes.sm,
            button: {
              color: "#5B60E3",
              fontSize: theme.fontSizes.sm,
            },
          })}
          my="xs"
        >
          {idea?.description?.replace(/<[^>]*>/g, "")}
        </Spoiler>
      </Box>
      <Flex align="center" justify="space-between">
        <Box mr="sm">
          <Text fz="xs" c="dimmed">
              Owner
          </Text>
          <Text  fz="xs" fw={500} tt="capitalize" >
            {idea?.owner?.name || '-'}
          </Text>
        </Box>
        <Box mr="sm">
          <Text fz="xs" c="dimmed">
              Status
          </Text>
          <Text fz="xs" fw={500} tt="capitalize">
            {idea?.status}
          </Text>
        </Box>
        <Box mr="sm">
          <Text fz="xs" c="dimmed">
              Priority
          </Text>
          <Text fz="xs" fw={500} tt="capitalize">
            {idea?.priority || "-"}
          </Text>
        </Box>
        <Box mr="sm">
          <Text fz="xs" c="dimmed">
              Product area
          </Text>
          <Text fz="xs" fw={500} tt="capitalize">
            {idea?.componentObj?.name|| "-"}
          </Text>
        </Box>
        <Box mr="sm">
          <Text fz="xs" c="dimmed">
              Accounts
          </Text>
          <Text fz="xs" fw={500}>
            {idea?.aggregations?.accountsCount?.total || "-"}
          </Text>
        </Box>
      </Flex>
    </Card>
  );
}


export default Search;
